import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import type { PartnerReferralCode } from '@Types/partnerReferralCode';
import type { RegistrationCode } from '@Types/registrationCode';
import { useInviteCodes } from '@Hooks/useInviteCodes';
import { useCreatePartnerReferralCode } from '@Hooks/useCreatePartnerReferralCode';
import { useUpdatePartnerReferralCode } from '@Hooks/useUpdatePartnerReferralCode';
import { MultiSelectCombobox } from '../MultiSelectCombobox';

const CODE_PATTERN = /^[A-Z0-9_-]+$/;

const INVITE_CODE_OPTIONS_PARAMS: Record<string, string> = {
  'page[size]': '-1',
  'sort': 'code',
};

const FormSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, { message: 'Code is required.' })
    .regex(CODE_PATTERN, {
      message: 'Use only letters, numbers, dashes and underscores.',
    }),
  description: z.string().optional(),
  registrationCodeIds: z
    .array(z.string())
    .min(1, { message: 'Select at least one invite code.' }),
});

type FormValues = z.input<typeof FormSchema>;

export interface PartnerReferralCodeSheetProps {
  partnerReferralCode?: PartnerReferralCode | null;
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
  onSuccess?: () => void;
}

const toFormValues = (
  partnerReferralCode?: PartnerReferralCode | null,
): FormValues => ({
  code: partnerReferralCode?.code ?? '',
  description: partnerReferralCode?.description ?? '',
  registrationCodeIds: partnerReferralCode?.registrationCodeIds ?? [],
});

const toOptionLabel = (registrationCode: RegistrationCode) => {
  const firstPromoDetail = registrationCode.promoDetails?.[0];
  return firstPromoDetail
    ? `${registrationCode.code} — ${firstPromoDetail}`
    : registrationCode.code;
};

export default function PartnerReferralCodeSheet({
  partnerReferralCode,
  isOpen,
  setIsOpen,
  onSuccess,
}: PartnerReferralCodeSheetProps) {
  const isEditMode = !!partnerReferralCode;

  const createMutation = useCreatePartnerReferralCode();
  const updateMutation = useUpdatePartnerReferralCode();

  const inviteCodes = useInviteCodes({
    searchParams: INVITE_CODE_OPTIONS_PARAMS,
    enabled: isOpen,
  });

  const inviteCodeItems = useMemo(
    () =>
      (inviteCodes.data?.data ?? []).map((registrationCode) => ({
        value: registrationCode.id,
        label: toOptionLabel(registrationCode),
      })),
    [inviteCodes.data],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toFormValues(partnerReferralCode),
  });

  useEffect(() => {
    if (isOpen) {
      form.reset(toFormValues(partnerReferralCode));
    }
  }, [partnerReferralCode, isOpen, form]);

  const onSubmit = async (values: FormValues) => {
    form.clearErrors('root');

    const availableIds = new Set(inviteCodeItems.map((item) => item.value));
    const description = values.description?.trim();
    const payload = {
      code: values.code.trim().toUpperCase(),
      description: description ? description : null,
      registrationCodeIds: values.registrationCodeIds.filter((id) =>
        availableIds.has(id),
      ),
    };

    if (payload.registrationCodeIds.length === 0) {
      form.setError('registrationCodeIds', {
        type: 'manual',
        message: 'Select at least one invite code.',
      });
      return;
    }

    try {
      if (isEditMode && partnerReferralCode) {
        await updateMutation.mutateAsync({
          id: partnerReferralCode.id,
          payload,
        });
      } else {
        await createMutation.mutateAsync(payload);
      }

      onSuccess?.();
      setIsOpen(false);
    } catch (error) {
      form.setError('root', {
        type: 'server',
        message:
          error instanceof Error
            ? error.message
            : 'We were unable to save this Partner Referral Code. Please check your changes and try again.',
      });
    }
  };

  const handleOnOpenChange = (newIsSheetOpen: boolean) => {
    setIsOpen(newIsSheetOpen);

    if (!newIsSheetOpen) {
      form.reset();
    }
  };

  const rootError = form.formState.errors.root?.message;
  const isSubmitting =
    form.formState.isSubmitting ||
    createMutation.isPending ||
    updateMutation.isPending;

  return (
    <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
      <SheetContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
            <div className="flex h-full flex-col">
              <SheetHeader>
                <SheetTitle>
                  {isEditMode
                    ? `Edit ${partnerReferralCode.code}`
                    : 'Create Partner Referral Code'}
                </SheetTitle>
              </SheetHeader>

              <SheetBody className="grow overflow-auto">
                {rootError && (
                  <div className="mb-4">
                    <Alert variant="destructive">
                      <AlertTitle>Something went wrong</AlertTitle>
                      <AlertDescription>{rootError}</AlertDescription>
                    </Alert>
                  </div>
                )}

                <div className="space-y-6 py-3">
                  <FormField
                    control={form.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Code</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. PORSCHE"
                            {...field}
                            onChange={(e) =>
                              field.onChange(e.target.value.toUpperCase())
                            }
                          />
                        </FormControl>
                        <p className="text-muted-foreground text-sm">
                          Prospects type this into the invitation code box.
                          It cannot match an existing invite code.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Optional description..."
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="registrationCodeIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Invite Codes</FormLabel>
                        <MultiSelectCombobox
                          items={inviteCodeItems}
                          selectedValues={field.value ?? []}
                          onChange={field.onChange}
                          placeholder="Select invite codes..."
                          disabled={inviteCodes.isLoading}
                          loading={inviteCodes.isLoading}
                          inModal
                        />
                        <p className="text-muted-foreground text-sm">
                          Prospects have access to these promos when using this
                          partner code.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </SheetBody>

              <SheetFooter>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  disabled={inviteCodes.isLoading}
                >
                  {isEditMode ? 'Update' : 'Create'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleOnOpenChange(false)}
                >
                  Cancel
                </Button>
              </SheetFooter>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
